import { useEffect, useRef } from 'react';
import SEO from '../components/SEO';
import './About.css';

export default function About() {
  const layoutRef = useRef(null);

  useEffect(() => {
    const els = layoutRef.current?.querySelectorAll('.about__animate');
    if (!els) return;
    els.forEach((el, i) => {
      // Shapes animate in first
      setTimeout(() => el.classList.add('about__animate--blocks'), 120 * i);
      // Text follows after shapes finish
      setTimeout(() => el.classList.add('about__animate--text'), 120 * i + 400);
    });
  }, []);

  return (
    <main id="main-content" className="about-page">
      <SEO
        title="About"
        description="Learn about Elijah Moreno's research on Indigenous governance, institutional economics, and federal Indian policy at Cornell University."
        path="/about"
      />

      <div className="about__layout" ref={layoutRef}>
        {/* Lead bio — large, prominent, no heading */}
        <div className="about__lead about__animate">
          <div className="about__block about__block--lead" />
          <div className="about__block about__block--lead-accent" />
          <div className="about__text-content">
            <p className="about__lead-text">
              Hi, I'm Elijah Moreno, an Indigenous social scientist studying
              how governance shapes economic and social outcomes. I am neurodivergent,
              and I think that shapes how I see the world. I tend to notice patterns
              other people miss, and to question structures most of us are taught to
              take for granted. A lot of my work is grounded in Indian Country, where
              federal policy has repeatedly reshaped tribal governance.
            </p>
            <p className="about__lead-sub">
              I am an enrolled member of the Coastal Band of the Chumash Nation, a
              non-federally recognized tribe in California. In my spare time, I am
              building{' '}
              <a href="https://lumecon.ai" target="_blank" rel="noopener noreferrer" className="about__lumecon-link">
                Lumecon.ai
              </a>
              , an AI-Native economic and policy analysis platform for governments.
            </p>
          </div>
        </div>

        {/* Two columns below: Approach + Education */}
        <div className="about__columns">
          <div className="about__col about__animate">
            <div className="about__block about__block--approach" />
            <div className="about__block about__block--approach-accent" />
            <div className="about__text-content">
              <h2 className="about__col-title">Approach</h2>
              <p className="about__col-text">
                I think like a sociologist, frame questions like a political scientist, and
                use the empirical tools of an economist. In practice, that means building
                theory, identifying causal effects, and designing systems that make complex
                institutional problems easier to understand.
              </p>
              <p className="about__col-text">
                I am particularly interested in settings where institutions matter
                most, where governance is disrupted or rebuilt, and in what those moments
                reveal about how policy actually works.
              </p>
            </div>
          </div>
          <div className="about__divider" />
          <div className="about__col about__animate">
            <div className="about__block about__block--education" />
            <div className="about__block about__block--education-accent" />
            <div className="about__text-content">
              <h2 className="about__col-title">Education</h2>
              <div className="about__edu-item">
                <span className="about__edu-degree">PhD Candidate, Public Policy</span>
                <a href="https://publicpolicy.cornell.edu/people/elijah-moreno/" target="_blank" rel="noopener noreferrer" className="about__edu-school">Cornell University</a>
              </div>
              <div className="about__edu-item">
                <span className="about__edu-degree">Master of Public Policy</span>
                <span className="about__edu-school">Cornell University</span>
              </div>
              <div className="about__edu-item">
                <span className="about__edu-degree">Bachelor's Degree</span>
                <span className="about__edu-school">Dartmouth College</span>
              </div>
            </div>
          </div>
        </div>

        {/* Affiliations & Awards */}
        <div className="about__credentials">
          <div className="about__credential-section about__animate">
            <div className="about__block about__block--affiliations" />
            <div className="about__block about__block--affiliations-accent" />
            <div className="about__text-content">
              <h2 className="about__col-title">Affiliations</h2>

              <h3 className="about__cred-subhead">Current</h3>
              <ul className="about__cred-list">
                <li>
                  <span className="about__cred-org">Cornell Population Center</span>
                  <span className="about__cred-role">Affiliate, 2023–present</span>
                </li>
                <li>
                  <span className="about__cred-org">Cornell Graduate School</span>
                  <span className="about__cred-role">Dean's Excellence Fellow, 2023–present</span>
                </li>
                <li>
                  <span className="about__cred-org">Harvard Kennedy School, Stone Program in Wealth Distribution, Inequality, and Social Policy</span>
                  <span className="about__cred-role">Inaugural PhD Student Visitor, 2025–2026</span>
                </li>
              </ul>

              <h3 className="about__cred-subhead">Past</h3>
              <ul className="about__cred-list">
                <li>
                  <span className="about__cred-org">Harvard Project on Indigenous Governance and Development</span>
                  <span className="about__cred-role">Research Affiliate, 2024–2025</span>
                </li>
                <li>
                  <span className="about__cred-org">Center for Indian Country Development, Federal Reserve Bank of Minneapolis</span>
                  <span className="about__cred-role">Visiting Scholar, 2023–2024</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="about__divider about__divider--vertical" />

          <div className="about__credential-section about__animate">
            <div className="about__block about__block--awards" />
            <div className="about__block about__block--awards-accent" />
            <div className="about__text-content">
              <h2 className="about__col-title">Awards &amp; Grants</h2>
              <ul className="about__cred-list">
                <li>
                  <span className="about__cred-org">Cornell Brooks School of Public Policy</span>
                  <span className="about__cred-role">Brooks Public Engagement Award, 2026</span>
                </li>
                <li>
                  <span className="about__cred-org">Cornell Brooks School of Public Policy</span>
                  <span className="about__cred-role">Outstanding Teaching Assistant Award, Introduction to Public Policy (PUBPOL 2301), Spring 2025</span>
                </li>
                <li>
                  <span className="about__cred-org">Cornell Brooks School of Public Policy</span>
                  <span className="about__cred-role">Racial Equity PhD Research Grant, Spring 2025</span>
                  <span className="about__cred-detail">"The Price of Delay: The Economic Consequences of State Legitimacy – Evidence from Native American Tribes"</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </main>
  );
}
